import type { MobileEventDto, MobileLeaveDto, MobileProfile } from './mobile';
import { z } from 'zod';
import { CASE_DOCUMENT_CATEGORY_LABELS, type CaseDocumentCategory } from './documents';
import { datePl, TASK_TYPES, type TaskStatus, type TaskType } from './planning';
import { type FieldProtocol, missingProtocols, type ProtocolSlot } from './protocol';
import './schemas';

/**
 * Praca w terenie (P5, docs/05): pakiet zadania na telefon (tylko dozwolone pola — bez danych finansowych),
 * operacje z telefonu (idempotentne, z identyfikatorem nadanym na telefonie) i wymagania zakończenia —
 * ta sama funkcja liczy je na telefonie (offline) i na serwerze.
 */

export interface ChecklistDef {
  label: string;
  required: boolean;
  requiresPhoto: boolean;
  valueType: 'liczba' | 'tekst' | null;
  unit: string;
}

const item = (label: string, o: Partial<Omit<ChecklistDef, 'label'>> = {}): ChecklistDef => ({ label, required: true, requiresPhoto: false, valueType: null, unit: '', ...o });

/** Domyślne listy czynności typów zadań (edycja list — ustawienia procesów, kolejny etap). */
export const CHECKLIST_DEFAULTS: Record<TaskType, ChecklistDef[]> = {
  wizja: [
    item('Oględziny miejsca montażu'),
    item('Zdjęcia miejsca montażu / kotłowni', { requiresPhoto: true }),
    item('Wstępne wymiary i uwagi', { valueType: 'tekst', required: false }),
  ],
  pomiary: [item('Pomiary wykonane'), item('Wynik pomiaru', { valueType: 'liczba', unit: '', required: false }), item('Zdjęcia punktów pomiarowych', { requiresPhoto: true, required: false })],
  montaz: [
    item('Zabezpieczenie miejsca pracy'),
    item('Montaż urządzeń zgodnie z projektem', { requiresPhoto: true }),
    item('Podłączenie instalacji'),
    item('Próba szczelności / test instalacji', { valueType: 'tekst' }),
    item('Uporządkowanie miejsca pracy', { requiresPhoto: true }),
    item('Instruktaż klienta', { required: false }),
  ],
  uruchomienie: [
    item('Sprawdzenie instalacji przed uruchomieniem'),
    item('Uruchomienie i parametryzacja'),
    item('Ciśnienie w instalacji', { valueType: 'liczba', unit: 'bar' }),
    item('Tabliczka znamionowa — zdjęcie', { requiresPhoto: true }),
    item('Instruktaż klienta'),
  ],
  serwis: [item('Diagnoza usterki', { valueType: 'tekst' }), item('Naprawa / wymiana części'), item('Test działania po naprawie')],
  przeglad: [
    item('Przegląd urządzenia'),
    item('Czyszczenie / wymiana filtrów'),
    // pomiary właściwe branży (np. temperatura nawiewu klimatyzacji) — w liście przeglądu branży (QA5 P30)
    item('Zdjęcie urządzenia', { requiresPhoto: true, required: false }),
  ],
  inne: [item('Prace wykonane')],
  poprawka: [item('Poprawki wykonane zgodnie z uwagami klienta'), item('Zdjęcia po poprawce', { requiresPhoto: true }), item('Klient potwierdził poprawki', { required: false })],
  // „Moje zadanie” — bez czynności
  wlasne: [],
};

/** Minimalna liczba zdjęć do zakończenia zadania. */
export const MIN_PHOTOS: Record<TaskType, number> = { wizja: 1, pomiary: 0, montaz: 2, uruchomienie: 1, serwis: 1, przeglad: 0, inne: 0, poprawka: 1, wlasne: 0 };

export const BLOCKER_CATEGORIES = {
  brak_materialu: 'Brak materiału',
  brak_dostepu: 'Brak dostępu do miejsca / klienta',
  pogoda: 'Warunki pogodowe',
  decyzja_klienta: 'Decyzja klienta',
  problem_techniczny: 'Problem techniczny',
  inne: 'Inne',
} as const;
export type BlockerCategory = keyof typeof BLOCKER_CATEGORIES;

// ── pakiet zadania (serwer → telefon) ─────────────────────────────────────

export interface FieldChecklistItem {
  id: string;
  position: number;
  /** branża, której dotyczy czynność (wspólna realizacja kilku branż — czynności pogrupowane); null — ogólna */
  tradeCode?: string | null;
  tradeName?: string | null;
  label: string;
  required: boolean;
  requiresPhoto: boolean;
  valueType: 'liczba' | 'tekst' | null;
  unit: string;
  done: boolean;
  value: string;
  doneByName: string | null;
  doneAt: string | null;
  version: number;
}

export interface FieldPhoto {
  id: string;
  checklistItemId: string | null;
  takenAt: string;
  authorName: string;
  /** plik dotarł na serwer */
  uploaded: boolean;
  /** skrót pliku — wchodzi do treści protokołu */
  sha256: string;
}

/** Rodzaje dokumentów sprawy, które trafiają na telefon (techniczne) — bez korespondencji, dotacji i „inne” (docs/05 rozdz. 2). */
/**
 * Dokumenty sprawy widoczne dla ekipy w aplikacji mobilnej. Bez korespondencji i dotacji (dane osobowe i finansowe klienta).
 * „Inne” — od 29.10.2026 (to rodzaj domyślny przy dodawaniu, ekipa nie widziała np. szkicu dodanego przez opiekuna).
 */
export const FIELD_DOCUMENT_CATEGORIES: readonly CaseDocumentCategory[] = ['projekt', 'dokumentacja', 'zdjecia', 'inne'];

/**
 * Plik sprawy przy zadaniu (docs/05 rozdz. 2): zdjęcie z innej pracy w tej sprawie (wizja, pomiary, montaż…)
 * albo dokument techniczny dołączony w biurze. Na telefonie są tylko metadane; plik pobiera się osobno.
 */
export interface FieldCaseFile {
  id: string;
  kind: 'zdjecie' | 'dokument';
  fileName: string;
  mime: string;
  size: number;
  sha256: string;
  /** zdjęcie — czas wykonania; dokument — dodania */
  at: string;
  /** kto zrobił zdjęcie / dodał dokument */
  authorName: string;
  /** zadanie, z którego pochodzi zdjęcie (null — dokument z biura); data — termin zadania */
  task: { number: string; taskType: TaskType; date: string | null } | null;
  /** czynność z listy, do której zrobiono zdjęcie */
  checklistLabel: string | null;
  /** dokument: rodzaj i opis */
  category: CaseDocumentCategory | null;
  description: string;
}

/** Podpis źródła pliku: „Wizja lokalna ZD/2026/0014 · 29.10.2026 · Jan Kowalski” albo „Projekt · dodał(a) Anna Nowak · 12.10.2026”. */
export function caseFileSource(f: Pick<FieldCaseFile, 'task' | 'at' | 'authorName' | 'checklistLabel' | 'category'>): string {
  if (f.task) return [`${TASK_TYPES[f.task.taskType]?.label ?? 'Zadanie'} ${f.task.number}`, datePl(f.at), f.authorName, f.checklistLabel].filter(Boolean).join(' · ');
  return [f.category ? CASE_DOCUMENT_CATEGORY_LABELS[f.category] : 'Dokument', f.authorName ? `dodał(a) ${f.authorName}` : '', datePl(f.at)].filter(Boolean).join(' · ');
}

/**
 * Pakiet zadania — wyłącznie pola z tej listy (docs/05 rozdz. 2). Nigdy: ceny, kwoty, wartości umów, rabaty,
 * marże, koszty, dane płatnika, oferty, umowy, harmonogramy, wpłaty, notatki wewnętrzne handlowca.
 */
export interface TaskPackage {
  id: string;
  rev: string;
  number: string;
  taskType: TaskType;
  status: TaskStatus;
  title: string;
  /** notatka biura dla ekipy (opis zadania) */
  officeNote: string;
  dateFrom: string | null;
  dateTo: string | null;
  timeFrom: string | null;
  timeTo: string | null;
  /** dni, w które użytkownik jest przydzielony */
  myDays: string[];
  teamName: string | null;
  crew: string[];
  caseNumber: string | null;
  clientName: string | null;
  site: { address: string; city: string; lat: number | null; lng: number | null } | null;
  contact: { name: string; phone: string } | null;
  /** adres e-mail klienta — propozycja adresu wysyłki protokołu (klient potwierdza przy podpisie) */
  clientEmail: string | null;
  scopes: { tradeCode: string; tradeName: string; description: string }[];
  /**
   * urządzenia do montażu — każda pozycja umowy / aneksu osobno, w kolejności: umowa, potem aneksy (QA P3).
   * `annex` — „A1”, gdy pozycja pochodzi z aneksu (starsze serwery nie wysyłają).
   */
  equipment: { name: string; quantity: number; unit: string; annex?: string | null }[];
  /**
   * QA P2: aneksy podpisane po zaplanowaniu zadania, których prace weszły do tego zadania — „Zmiana zakresu: + …”
   * (bez numerów umów i kwot; starsze serwery nie wysyłają).
   */
  scopeChanges?: { annex: string; signedAt: string; items: { name: string; quantity: number; unit: string }[] }[];
  /**
   * QA qa10311123 3.2: aneksy wydane do zakresu zadania, które czekają na podpis klienta — „Aneks A1 czeka na podpis
   * klienta: + …” na ekranie zadania i protokołu (bez numerów umów i kwot; starsze serwery nie wysyłają).
   */
  pendingAnnexes?: { annex: string; issuedAt: string | null; items: { name: string; quantity: number; unit: string }[] }[];
  requiresConditions: boolean;
  conditionsMet: boolean;
  /** np. „brak zaliczki” — bez kwot */
  conditionsText: string;
  checklist: FieldChecklistItem[];
  minPhotos: number;
  notes: { id: string; body: string; authorName: string; createdAt: string; mine: boolean }[];
  photos: FieldPhoto[];
  materials: { id: string; name: string; quantity: number; unit: string; authorName: string; mine: boolean }[];
  blockers: { id: string; category: BlockerCategory; description: string; reportedAt: string; reportedByName: string; resolvedAt: string | null; resolution: string }[];
  /** protokoły do wypełnienia (szablon z wersją, dane firmy zakresu, urządzenia) — P6 */
  protocolSlots: ProtocolSlot[];
  protocols: FieldProtocol[];
  /** zgłoszenie serwisowe (zadania przeglądu / serwisu) */
  ticket: {
    number: string;
    type: string;
    description: string;
    /** zgłoszenie gwarancyjne (D14) — starsze serwery nie wysyłają */
    warranty?: boolean;
    /** urządzenia zgłoszenia z gwarancją z karty urządzenia */
    devices?: { name: string; serial: string; warrantyUntil: string | null }[];
    /** zaległe przeglądy instalacji (opis: urządzenie / instalacja, termin) — do zrobienia przy okazji wizyty */
    overdueReviews?: string[];
    /** QA7 P12: przeglądy wykonane po tolerancji w ostatnich 12 miesiącach (opis) — gwarancja może być zagrożona */
    lateInspections?: string[];
    /** docs/26: rozliczenie (tylko z uprawnieniem `serwis.platnosci`; starsze serwery nie wysyłają) */
    charge?: import('./service-charges').FieldServiceCharge | null;
  } | null;
  /** zapotrzebowanie materiałów (docs/14): potrzeby z umowy dla zakresów zadania i zgłoszenia ekipy — bez cen */
  materialRequests?: FieldMaterials | null;
  /** dokumenty techniczne sprawy i zdjęcia z innych prac w sprawie (starsze pakiety na telefonie mogą go nie mieć) */
  caseFiles: FieldCaseFile[];
}

export interface FieldDemandLine {
  /** null — pozycja dodana na telefonie, jeszcze niewysłana */
  id: string | null;
  itemId: string | null;
  name: string;
  unit: string;
  quantity: number;
  reserved: number;
  issued: number;
  shortage: number;
  additional: boolean;
}

export interface FieldDemand {
  /** identyfikator nadany na telefonie (klucz zgłoszenia) */
  key: string;
  id: string | null;
  status: 'otwarte' | 'zrealizowane' | 'anulowane';
  comment: string;
  photoIds: string[];
  /** zmiana czeka na wysłanie */
  pending: boolean;
  lines: FieldDemandLine[];
}

export interface FieldMaterials {
  contractNeeds: { itemId: string; name: string; unit: string; quantity: number; reserved: number; issued: number }[];
  requests: FieldDemand[];
  /** pakiety przygotowane przez magazyn: co przygotowano, czego brakuje, gdzie i kiedy odebrać, co wydano */
  packages?: FieldPackage[];
}

export interface FieldPackage {
  id: string;
  number: string;
  status: string;
  statusLabel: string;
  plannedPickup: string | null;
  pickupPlace: string;
  note: string;
  lines: { name: string; unit: string; required: number; picked: number; issued: number; missing: number; substituteNote: string }[];
  issues: { at: string; recipientName: string | null; issuedByName: string | null }[];
}

export interface SyncResponse {
  serverTime: string;
  /** pakiety nowe lub zmienione względem `known` */
  packages: TaskPackage[];
  /** zadania odebrane (spoza okna lub bez przydziału) — do usunięcia z telefonu po wysłaniu zmian */
  removed: string[];
  /** moduły i słowniki aplikacji pracownika (starsze serwery nie wysyłają — telefon zakłada same zadania) */
  profile?: MobileProfile;
  /** wydarzenia firmowe pracownika (moduł „Wydarzenia firmowe”) */
  events?: MobileEventDto[];
  /** wnioski urlopowe (moduł „Urlopy”): moje oraz czekające na moją decyzję */
  leave?: { mine: MobileLeaveDto[]; toDecide: MobileLeaveDto[] };
  /** moduł „Samochody” (Flota): przypisane auta i moje zgłoszenia — bez kosztów i dokumentów */
  fleet?: import('./fleet').MyFleetDto;
  /** powiadomienia na kanale „telefon” (docs/16) — dzwonek w aplikacji; nieprzeczytane i ostatnio przeczytane */
  notifications?: { items: import('./types').NotificationDto[]; unread: number };
}

// ── operacje (telefon → serwer) ───────────────────────────────────────────

const uuid = z.string().uuid();
const base = { id: uuid, taskId: uuid, deviceTime: z.string().datetime({ offset: true }), seq: z.number().int().min(0) };

export const fieldOpSchema = z.discriminatedUnion('type', [
  z.object({ ...base, type: z.literal('task.start'), data: z.object({}).default({}) }),
  z.object({
    ...base,
    type: z.literal('task.finish'),
    data: z.object({ note: z.string().max(2000).default(''), noProtocolReason: z.string().trim().max(500).default('') }).default({ note: '', noProtocolReason: '' }),
  }),
  z.object({
    ...base,
    type: z.literal('checklist.set'),
    data: z.object({ itemId: uuid, done: z.boolean(), value: z.string().max(500).default(''), baseVersion: z.number().int().min(0) }),
  }),
  z.object({ ...base, type: z.literal('note.add'), data: z.object({ noteId: uuid, body: z.string().trim().min(1).max(4000) }) }),
  z.object({
    ...base,
    type: z.literal('photo.add'),
    data: z.object({
      photoId: uuid,
      checklistItemId: uuid.nullable().default(null),
      sha256: z.string().regex(/^[0-9a-f]{64}$/),
      size: z.number().int().positive().max(25 * 1024 * 1024),
      mime: z.enum(['image/jpeg', 'image/png', 'image/webp']),
      takenAt: z.string().datetime({ offset: true }),
    }),
  }),
  z.object({
    ...base,
    type: z.literal('material.add'),
    data: z.object({ materialId: uuid, name: z.string().trim().min(1).max(200), quantity: z.number().positive().max(100000), unit: z.string().trim().max(10).default('szt.') }),
  }),
  z.object({
    ...base,
    type: z.literal('demand.save'),
    data: z.object({
      /** klucz zgłoszenia nadany na telefonie — nowe zgłoszenie albo zmiana istniejącego */
      demandKey: uuid,
      lines: z
        .array(
          z.object({
            id: uuid.nullable().default(null),
            itemId: uuid.nullable().default(null),
            description: z.string().trim().max(300).default(''),
            unit: z.string().trim().max(10).default('szt.'),
            quantity: z.number().positive().max(1_000_000),
            additional: z.boolean().default(false),
          }),
        )
        .max(100),
      comment: z.string().trim().max(2000).default(''),
      photoIds: z.array(uuid).max(10).default([]),
    }),
  }),
  z.object({
    ...base,
    type: z.literal('blocker.report'),
    data: z.object({
      blockerId: uuid,
      category: z.enum(['brak_materialu', 'brak_dostepu', 'pogoda', 'decyzja_klienta', 'problem_techniczny', 'inne']),
      description: z.string().trim().min(3).max(2000),
    }),
  }),
  z.object({
    ...base,
    type: z.literal('protocol.sign'),
    data: z.object({
      protocolId: uuid,
      slotKey: z.string().min(1).max(80),
      /** treść kanoniczna — serwer przelicza z niej skrót (niezgodność = odrzucenie) */
      content: z.record(z.string(), z.unknown()),
      contentHash: z.string().regex(/^[0-9a-f]{64}$/),
      signature: z.object({ strokes: z.array(z.array(z.tuple([z.number(), z.number(), z.number()])).max(5000)).min(1).max(200) }),
      signatureHash: z.string().regex(/^[0-9a-f]{64}$/),
    }),
  }),
  /** klienta nie ma przy odbiorze — protokół (treść z signMode) wysyłany do e-podpisu albo do podpisu na wydruku */
  z.object({
    ...base,
    type: z.literal('protocol.prepare'),
    data: z.object({
      protocolId: uuid,
      slotKey: z.string().min(1).max(80),
      content: z.record(z.string(), z.unknown()),
      contentHash: z.string().regex(/^[0-9a-f]{64}$/),
      /** e-podpis: kod potwierdzenia SMS-em (z numerem) albo e-mailem */
      channel: z.enum(['email', 'sms']).default('email'),
      phone: z.string().trim().max(20).default(''),
    }),
  }),
  /** podpis papierowy: zdjęcia podpisanego wydruku (wysłane jak zdjęcia zadania) — protokół „do podpisu” staje się podpisany */
  z.object({
    ...base,
    type: z.literal('protocol.paper'),
    data: z.object({
      protocolId: uuid,
      photoIds: z.array(uuid).min(1).max(10),
      signerName: z.string().trim().min(3).max(200),
      signedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    }),
  }),
]);
export type FieldOp = z.infer<typeof fieldOpSchema>;
/** operacja przed walidacją (pola z wartością domyślną są opcjonalne) */
export type FieldOpInput = z.input<typeof fieldOpSchema>;
export type FieldOpType = FieldOp['type'];

export const fieldOpsBatchSchema = z.object({ ops: z.array(z.unknown()).min(1).max(100) });

export type OpStatus = 'przyjeta' | 'odrzucona' | 'konflikt';
export interface OpResult {
  id: string;
  status: OpStatus;
  /** komunikat po polsku dla instalatora (odrzucenie / konflikt / oznaczenie do sprawdzenia) */
  message: string;
  /** przyjęta, ale oznaczona do sprawdzenia przez koordynatora */
  flagged: boolean;
}

export const deviceLoginSchema = z.object({
  /** login albo e-mail (nazwa pola „email” — zgodność ze starszymi wersjami aplikacji) */
  email: z.string().trim().toLowerCase().min(1).max(200),
  password: z.string().min(1).max(200),
  deviceId: uuid,
  deviceName: z.string().trim().min(1).max(100),
  platform: z.enum(['android', 'ios', 'web']),
  /** kod logowania dwuetapowego, gdy konto go wymaga */
  code: z.string().trim().max(20).optional(),
});

// ── aktywacja telefonu kodem QR (panel: Pracownicy → karta pracownika → Telefony) ─────────

/** Alfabet kodu aktywacji — bez znaków mylących się przy przepisywaniu (0/O, 1/I/L, U/V). */
export const ACTIVATION_ALPHABET = 'ABCDEFGHJKMNPQRSTWXYZ23456789';
/** Kod z QR albo przepisany ręcznie → postać kanoniczna (wielkie litery, bez kresek i spacji). */
export const normalizeActivationCode = (v: string) => v.toUpperCase().replace(/[^A-Z0-9]/g, '');
/** Zawartość kodu QR: adres serwera i kod (aplikacja odczytuje oba, pracownik niczego nie wpisuje). */
export const activationPayload = (server: string, code: string) => `bqcrm://aktywacja?s=${encodeURIComponent(server)}&k=${code}`;
export function parseActivationPayload(v: string): { server: string; code: string } | null {
  const m = /^bqcrm:\/\/aktywacja\?(.+)$/.exec(v.trim());
  if (!m) return null;
  const p = new URLSearchParams(m[1]);
  const server = p.get('s');
  const code = p.get('k');
  return server && code ? { server, code: normalizeActivationCode(code) } : null;
}

export const deviceActivateSchema = z.object({
  code: z.string().trim().min(8).max(40).transform(normalizeActivationCode),
  deviceId: uuid,
  deviceName: z.string().trim().min(1).max(100),
  platform: z.enum(['android', 'ios', 'web']),
});

export interface DeviceActivationDto {
  /** kod do przepisania (gdy aparat niedostępny), np. ABCD-EFGH-JKMN */
  code: string;
  expiresAt: string;
  /** zawartość kodu QR */
  payload: string;
  /** obraz kodu QR (data:image/svg+xml…) */
  qr: string;
  server: string;
}

export interface DeviceLoginResponse {
  token: string;
  /** środowisko demonstracyjne — aplikacja pokazuje baner „dane fikcyjne” */
  demo: boolean;
  user: { id: string; fullName: string; email: string };
  deviceId: string;
  /** wymagana zmiana hasła — aplikacja pokazuje ekran zmiany przed pracą */
  mustChangePassword?: boolean;
}

export interface UploadStatus {
  id: string;
  size: number;
  offset: number;
  complete: boolean;
}

export interface MobileDeviceDto {
  id: string;
  userId: string;
  name: string;
  platform: string;
  createdAt: string;
  lastSeenAt: string | null;
  revokedAt: string | null;
}

// ── wymagania zakończenia (telefon i serwer) ──────────────────────────────

export interface FinishCheck {
  label: string;
  ok: boolean;
}

/** Wymagania zakończenia. `noProtocolReason` — uzasadniony brak protokołu (np. klient nieobecny) zastępuje protokoły. */
export function finishRequirements(
  p: Pick<TaskPackage, 'checklist' | 'photos' | 'minPhotos' | 'status'> & Partial<Pick<TaskPackage, 'protocolSlots' | 'protocols'>>,
  noProtocolReason = '',
): FinishCheck[] {
  // zakończone spełnia warunek „rozpoczęte”; wstrzymane (blokada) — osobny komunikat
  const out: FinishCheck[] = [
    p.status === 'wstrzymane' ? { label: 'Zadanie wstrzymane — wznowienie po decyzji biura', ok: false } : { label: 'Zadanie rozpoczęte', ok: p.status === 'w_toku' || p.status === 'zakonczone' },
  ];
  // wspólna realizacja kilku branż — nazwy czynności się powtarzają, więc z kodem branży (np. „PC: Próba szczelności”)
  const manyTrades = new Set(p.checklist.map((c) => c.tradeCode).filter(Boolean)).size > 1;
  const name = (c: Pick<FieldChecklistItem, 'label' | 'tradeCode'>) => (manyTrades && c.tradeCode ? `${c.tradeCode}: ${c.label}` : c.label);
  for (const c of p.checklist.filter((x) => x.required)) {
    const noValue = c.valueType !== null && c.value.trim() === '';
    out.push({ label: c.done && noValue ? `${name(c)} — brak wyniku` : name(c), ok: c.done && !noValue });
  }
  for (const c of p.checklist.filter((x) => x.requiresPhoto && (x.required || x.done))) {
    out.push({ label: `Zdjęcie: ${name(c)}`, ok: p.photos.some((ph) => ph.checklistItemId === c.id) });
  }
  if (p.minPhotos > 0) out.push({ label: `Co najmniej ${p.minPhotos} zdj.`, ok: p.photos.length >= p.minPhotos });
  const slots = (p.protocolSlots ?? []).filter((s) => s.required);
  const missing = new Set(missingProtocols(slots, p.protocols ?? []).map((s) => s.key));
  const excused = noProtocolReason.trim().length >= 5;
  for (const s of slots) out.push({ label: `Protokół: ${s.template.title}${slots.length > 1 ? ` (${s.tradeName})` : ''}`, ok: !missing.has(s.key) || excused });
  return out;
}

/**
 * Braki listy czynności, które blokują podpis protokołu na telefonie (P5): wymagane czynności i ich wyniki, wymagane zdjęcia,
 * minimalna liczba zdjęć — bez stanu zadania i protokołów. Serwer liczy to samo przy przyjęciu protokołu (oznaczenie
 * „podpisany przy niepełnej checkliście” — podpisu nie odrzuca, bo telefon mógł pracować bez sieci).
 */
export function protocolChecklistGaps(p: Pick<TaskPackage, 'checklist' | 'photos' | 'minPhotos' | 'status'>, slot?: Pick<ProtocolSlot, 'tradeCode' | 'scopeIds'> | null): FinishCheck[] {
  // osobne protokoły branż (osobne umowy) — tylko czynności branż tego protokołu (i czynności wspólne bez branży)
  const trades = slot?.scopeIds.length ? slot.tradeCode.split('+') : null;
  const checklist = trades ? p.checklist.filter((c) => !c.tradeCode || trades.includes(c.tradeCode)) : p.checklist;
  return finishRequirements({ ...p, checklist, protocolSlots: [], protocols: [] })
    .slice(1)
    .filter((r) => !r.ok);
}

/** Dane z terenu dla biura (okno zadania). */
export interface TaskFieldDto {
  checklist: (FieldChecklistItem & { flagged: boolean })[];
  notes: TaskPackage['notes'];
  photos: FieldPhoto[];
  materials: TaskPackage['materials'];
  blockers: TaskPackage['blockers'];
  finish: FinishCheck[];
  protocols: (FieldProtocol & {
    pdfReady: boolean;
    mails: { id: string; kind: string; toEmail: string; status: string; sentAt: string | null; lastError: string | null }[];
    /** podpis zdalny (e-podpis / papier) — stan, ślad i akcje biura */
    signing?: import('./protocol').ProtocolSigningDto;
  })[];
  events: { id: number; type: string; userName: string | null; message: string; flagged: boolean; reviewed: boolean; at: string }[];
  can: { resolveBlocker: boolean; review: boolean; voidProtocol: boolean; resendProtocol: boolean };
}

export const TASK_EVENT_LABELS: Record<string, string> = {
  rozpoczecie: 'Rozpoczęcie',
  dolaczenie: 'Dołączenie do pracy',
  zakonczenie: 'Zakończenie',
  blokada: 'Zgłoszenie blokady',
  blokada_rozwiazana: 'Blokada rozwiązana',
  czynnosc_konflikt: 'Konflikt czynności',
  praca_w_anulowanym: 'Praca w anulowanym zadaniu',
  zmiana_spoza_skladu: 'Zmiana od osoby spoza składu',
  protokol: 'Protokół podpisany',
  protokol_niezgodny: 'Protokół odrzucony — niezgodny skrót',
  protokol_niepelny: 'Protokół podpisany przy niepełnej checkliście',
  bez_protokolu: 'Zakończono bez protokołu',
  protokol_uniewazniony: 'Protokół unieważniony',
  przelozenie: 'Przełożenie terminu',
  zmiana_skladu: 'Zmiana składu ekipy',
};

/**
 * QA7 P20: czynność, która wymaga tylko zdjęcia (bez wyniku do wpisania) — dodanie pierwszego zdjęcia do niej odhacza ją
 * (telefon, w chwili dodania zdjęcia; działa także bez sieci). Czynności z wynikiem monter odhacza sam.
 */
export function photoCompletesItem(c: Pick<FieldChecklistItem, 'requiresPhoto' | 'valueType' | 'done'>, taskStatus: TaskPackage['status']): boolean {
  return taskStatus === 'w_toku' && c.requiresPhoto && c.valueType === null && !c.done;
}
